/**
 * A message that was sent while the page left for the silent sign-in (issue #486).
 *
 * After a long idle the first send is refused, the token renewal can fail, and the one
 * way back without asking is a full-page trip through the identity provider. That trip
 * ends this page and everything in its memory, including the message just sent, which
 * the backend never received. So every send is written here before it goes out and
 * removed as soon as the backend answers, and what is still here when the app starts is a
 * send the page left in the middle of. The app sends it again.
 *
 * `sessionStorage` because it survives the redirect and belongs to this tab alone. The
 * text only: staged images were uploaded under a conversation that never started.
 */

import { SigningInAgainError } from '../api/http';

const KEY = 'mikeai.pending-send';

/** Older than this, the person has moved on and resending would surprise them. The
    silent sign-in comes back within seconds. */
export const PENDING_SEND_MAX_AGE_MS = 5 * 60_000;

export interface PendingSend {
  sessionId: string;
  query: string;
  product: string | null;
  version: string | null;
  isNew: boolean;
  savedAt: number;
}

/** `sessionStorage`, or null where the browser refuses it. */
function defaultStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function savePendingSend(
  send: Omit<PendingSend, 'savedAt'>,
  now = Date.now(),
  store: Storage | null = defaultStore(),
): void {
  try {
    store?.setItem(KEY, JSON.stringify({ ...send, savedAt: now }));
  } catch {
    // Without storage the message is lost on a redirect, as it was before this existed.
  }
}

export function clearPendingSend(store: Storage | null = defaultStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // As above.
  }
}

/** Remove and return the pending send, if there is one recent enough to resend. */
export function takePendingSend(
  now = Date.now(),
  store: Storage | null = defaultStore(),
): PendingSend | null {
  let raw: string | null = null;
  try {
    raw = store?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;
  clearPendingSend(store);
  try {
    const send = JSON.parse(raw) as PendingSend;
    if (typeof send.sessionId !== 'string' || typeof send.query !== 'string') return null;
    if (!send.query.trim() || now - send.savedAt > PENDING_SEND_MAX_AGE_MS) return null;
    return send;
  } catch {
    return null;
  }
}

/**
 * Whether this page load has looked for a pending send, which it may do only once.
 *
 * Per page load and not per mount: `AuthGate` swaps the app for another screen whenever
 * the session drops for a moment, and the remount would find the send the first mount
 * still has in flight and send it a second time. Only a fresh page load can hold a send
 * that nothing is sending.
 */
let checkedThisPage = false;

/** `takePendingSend`, at most once per page load; every later call answers null. */
export function takePendingSendForThisPage(
  now = Date.now(),
  store: Storage | null = defaultStore(),
): PendingSend | null {
  if (checkedThisPage) return null;
  checkedThisPage = true;
  return takePendingSend(now, store);
}

/**
 * Pass a send's stream through, keeping the send stored until the backend answers.
 *
 * Stored before the first request, removed at the first event, and removed when the
 * stream ends or fails in any other way. Kept only when the page is leaving for the silent
 * sign-in, which is the one case where nothing in this page will finish the send.
 */
export async function* trackPendingSend<T>(
  send: Omit<PendingSend, 'savedAt'>,
  stream: AsyncIterable<T>,
  store: Storage | null = defaultStore(),
): AsyncGenerator<T> {
  savePendingSend(send, Date.now(), store);
  let answered = false;
  try {
    for await (const event of stream) {
      if (!answered) {
        answered = true;
        clearPendingSend(store);
      }
      yield event;
    }
  } catch (err) {
    if (err instanceof SigningInAgainError) throw err;
    clearPendingSend(store);
    throw err;
  }
  clearPendingSend(store);
}
